import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { Navbar } from '@/components/layout/Navbar';
import { Footer } from '@/components/layout/Footer';
import { Button } from '@/components/ui/button';
import { 
  Check, Crown, Zap, Shield, Star, Loader2, ArrowRight, Building2
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PricingPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [billingInterval, setBillingInterval] = useState('monthly');
  const [subscribing, setSubscribing] = useState(null);
  const [subscription, setSubscription] = useState(null);

  const user = (() => {
    try { return JSON.parse(localStorage.getItem('sfretire_user') || 'null'); } 
    catch { return null; }
  })();
  const token = localStorage.getItem('sfretire_token');

  useEffect(() => {
    fetchPlans();
    if (token && user?.role === 'entreprise') {
      fetchSubscription();
    }
  }, []);

  // Poll payment status if returning from Stripe
  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    const payment = searchParams.get('payment');
    if (sessionId && payment === 'success') {
      pollPaymentStatus(sessionId);
    }
  }, [searchParams]);

  const fetchPlans = async () => {
    try {
      const res = await fetch(`${API_URL}/api/payments/plans`);
      const data = await res.json();
      setPlans(data.plans || []);
    } catch (err) {
      console.error('Failed to fetch plans:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchSubscription = async () => {
    try {
      const res = await fetch(`${API_URL}/api/payments/subscription`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      setSubscription(data);
    } catch (err) {
      console.error('Failed to fetch subscription:', err);
    }
  };

  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    const maxAttempts = 5;
    if (attempts >= maxAttempts) {
      toast.info('Vérification du paiement en cours. Rechargez la page dans quelques instants.');
      return;
    }

    try {
      const res = await fetch(`${API_URL}/api/payments/status/${sessionId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();

      if (data.payment_status === 'paid') {
        toast.success('Paiement réussi ! Votre abonnement est actif.');
        fetchSubscription();
        return;
      } else if (data.status === 'expired') {
        toast.error('La session de paiement a expiré.');
        return;
      }

      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), 2000);
    } catch (err) {
      console.error('Payment status check failed:', err);
    }
  };

  const handleSubscribe = async (planId) => {
    if (!token || !user) {
      toast.error('Veuillez vous connecter en tant qu\'entreprise.');
      return;
    }
    if (user.role !== 'entreprise') {
      toast.error('Les abonnements sont réservés aux entreprises.');
      return;
    }

    setSubscribing(planId);
    try {
      const res = await fetch(`${API_URL}/api/payments/subscribe`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          plan_id: planId,
          origin_url: window.location.origin
        })
      });

      const data = await res.json();
      if (data.url) {
        window.location.href = data.url;
      } else {
        toast.error(data.detail || 'Erreur lors de la création du paiement');
      }
    } catch (err) {
      toast.error('Erreur de connexion');
    } finally {
      setSubscribing(null);
    }
  };

  const monthlyPlans = plans.filter(p => p.interval === 'monthly');
  const annualPlans = plans.filter(p => p.interval === 'annual');
  const displayPlans = billingInterval === 'monthly' ? monthlyPlans : annualPlans;

  const planIcons = {
    'starter': <Zap size={24} />,
    'pro': <Crown size={24} />
  };

  const planColors = {
    'starter': 'from-blue-500/10 to-cyan-500/10 border-blue-200',
    'pro': 'from-amber-500/10 to-orange-500/10 border-amber-200'
  };

  return (
    <div className="min-h-screen bg-background" data-testid="pricing-page">
      <Navbar />
      
      <main className="pt-24 pb-20">
        {/* Header */}
        <div className="max-w-5xl mx-auto px-4 text-center mb-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 text-primary text-sm font-medium mb-6">
              <Star size={16} />
              Tarification simple et transparente
            </span>
            <h1 className="text-4xl sm:text-5xl font-serif font-bold text-foreground mb-4">
              Choisissez votre <span className="text-primary">abonnement</span>
            </h1>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Accédez aux meilleurs experts retraités et développez votre entreprise avec des talents expérimentés.
            </p>
          </motion.div>

          {/* Billing Toggle */}
          <div className="flex items-center justify-center gap-4 mt-10" data-testid="billing-toggle">
            <span className={`text-sm font-medium ${billingInterval === 'monthly' ? 'text-foreground' : 'text-muted-foreground'}`}>
              Mensuel
            </span>
            <button
              onClick={() => setBillingInterval(billingInterval === 'monthly' ? 'annual' : 'monthly')}
              className={`relative w-14 h-7 rounded-full transition-colors ${billingInterval === 'annual' ? 'bg-primary' : 'bg-muted'}`}
              data-testid="billing-toggle-switch"
            >
              <span className={`absolute top-0.5 left-0.5 w-6 h-6 bg-white rounded-full shadow transition-transform ${billingInterval === 'annual' ? 'translate-x-7' : ''}`} />
            </button>
            <span className={`text-sm font-medium ${billingInterval === 'annual' ? 'text-foreground' : 'text-muted-foreground'}`}>
              Annuel
            </span>
            {billingInterval === 'annual' && (
              <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full font-medium">
                -17% / 2 mois offerts
              </span>
            )}
          </div>
        </div>

        {/* Plans */}
        {loading ? (
          <div className="flex justify-center py-20">
            <Loader2 className="animate-spin text-primary" size={40} />
          </div>
        ) : (
          <div className="max-w-5xl mx-auto px-4 grid md:grid-cols-2 gap-8">
            {displayPlans.map((plan, index) => {
              const tier = plan.id.split('_')[0];
              const isPro = tier === 'pro';
              const isActive = subscription?.plan === plan.id && subscription?.status === 'active';

              return (
                <motion.div
                  key={plan.id}
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.15 }}
                  className={`relative rounded-2xl border-2 p-8 bg-gradient-to-br ${planColors[tier] || 'border-border'} ${isPro ? 'ring-2 ring-primary/30' : ''}`}
                  data-testid={`plan-card-${plan.id}`}
                >
                  {isPro && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 bg-primary text-primary-foreground text-xs font-semibold rounded-full">
                      Populaire
                    </div>
                  )}

                  <div className="flex items-center gap-3 mb-4">
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${isPro ? 'bg-amber-500/20 text-amber-600' : 'bg-blue-500/20 text-blue-600'}`}>
                      {planIcons[tier]}
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-foreground">{plan.name}</h3>
                    </div>
                  </div>

                  <p className="text-sm text-muted-foreground mb-6">{plan.description}</p>

                  <div className="mb-8">
                    <span className="text-4xl font-bold text-foreground">
                      {plan.amount.toLocaleString('fr-FR')}
                    </span>
                    <span className="text-muted-foreground ml-1">
                      &euro; / {plan.interval === 'monthly' ? 'mois' : 'an'}
                    </span>
                    {plan.interval === 'annual' && (
                      <p className="text-sm text-muted-foreground mt-1">
                        soit {(plan.amount / 12).toFixed(0)}&euro; / mois
                      </p>
                    )}
                  </div>

                  <ul className="space-y-3 mb-8">
                    {plan.features?.map((feature, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm">
                        <Check size={18} className={`flex-shrink-0 mt-0.5 ${isPro ? 'text-amber-600' : 'text-blue-600'}`} />
                        <span className="text-foreground">{feature}</span>
                      </li>
                    ))}
                    <li className="flex items-start gap-2 text-sm">
                      <Shield size={18} className="flex-shrink-0 mt-0.5 text-green-600" />
                      <span className="text-foreground">Commission 10% par mission validée</span>
                    </li>
                  </ul>

                  {isActive ? (
                    <Button variant="outline" className="w-full h-12" disabled data-testid={`plan-active-${plan.id}`}>
                      <Check size={18} className="mr-2" /> Abonnement actif
                    </Button>
                  ) : (
                    <Button
                      variant={isPro ? 'gold' : 'default'}
                      className="w-full h-12"
                      onClick={() => handleSubscribe(plan.id)}
                      disabled={subscribing === plan.id}
                      data-testid={`subscribe-btn-${plan.id}`}
                    >
                      {subscribing === plan.id ? (
                        <><Loader2 className="animate-spin mr-2" size={18} /> Redirection...</>
                      ) : (
                        <>Souscrire <ArrowRight size={18} className="ml-2" /></>
                      )}
                    </Button>
                  )}
                </motion.div>
              );
            })}
          </div>
        )}

        {/* Commission Info */}
        <div className="max-w-5xl mx-auto px-4 mt-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="bg-card border border-border rounded-2xl p-8"
          >
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-xl bg-green-500/10 flex items-center justify-center flex-shrink-0">
                <Building2 size={24} className="text-green-600" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-foreground mb-2">Commission par mission</h3>
                <p className="text-muted-foreground">
                  En plus de votre abonnement, une commission de <strong>10%</strong> est appliquée 
                  sur le budget de chaque mission validée. Cette commission est payée lors de la validation 
                  d&apos;une candidature et couvre les frais de mise en relation et de gestion de la plateforme.
                </p>
              </div>
            </div>
          </motion.div>
        </div>

        {/* FAQ */}
        <div className="max-w-3xl mx-auto px-4 mt-16">
          <h2 className="text-2xl font-serif font-bold text-foreground text-center mb-8">Questions fréquentes</h2>
          <div className="space-y-4">
            {[
              {
                q: "Puis-je changer de formule ?",
                a: "Oui, vous pouvez passer du Starter au Pro à tout moment. La différence sera calculée au prorata."
              },
              {
                q: "Comment fonctionne la commission de 10% ?",
                a: "Lorsque vous validez une candidature pour une mission, une commission de 10% du budget de la mission est facturée. Elle couvre les services de mise en relation et de gestion."
              },
              {
                q: "Y a-t-il un engagement ?",
                a: "L&apos;abonnement mensuel est sans engagement. L&apos;abonnement annuel offre 2 mois gratuits et est renouvelé automatiquement."
              },
              {
                q: "Comment puis-je annuler ?",
                a: "Vous pouvez annuler votre abonnement à tout moment depuis votre tableau de bord. L&apos;accès reste actif jusqu&apos;à la fin de la période payée."
              }
            ].map((faq, i) => (
              <details key={i} className="group border border-border rounded-xl p-4 cursor-pointer">
                <summary className="font-medium text-foreground flex items-center justify-between">
                  {faq.q}
                  <ArrowRight size={16} className="text-muted-foreground group-open:rotate-90 transition-transform" />
                </summary>
                <p className="text-muted-foreground text-sm mt-3">{faq.a}</p>
              </details>
            ))}
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default PricingPage;
